import { RealtimeModes as modes } from "mobility-toolbox-js/api";
import { MaplibreLayer } from "mobility-toolbox-js/ol";
import { View } from "ol";
import { buffer, getCenter } from "ol/extent";
import Feature from "ol/Feature";
import GeoJSON from "ol/format/GeoJSON";
import Map from "ol/Map";
import { unByKey } from "ol/Observable";
import PropTypes from "prop-types";
import qs from "query-string";
import { Component } from "react";
import { connect } from "react-redux";

import { TRACK_EVENT_CATEGORY_MAP } from "../constants";
import {
  setIsFollowingVehicle,
  setSelectedLine,
  setSelectedVehicles,
  setStation,
  subscribeDeletedVehicles,
  subscribeStations,
  trackEvent,
  unsubscribeDepartures,
} from "../model/actions";
import AppPropTypes from "../model/propTypes";
import tralisAPI from "../TralisAPI";
import TralisLayer from "../TralisLayer";
import {
  addPeripherieLineIcons,
  addSourceAndLayers,
  areLineNameEqual,
  getRandomVehicle,
  isVehicleToHighlight,
} from "../utils";
import cleanHighlight from "../utils/cleanHighlight";

import AutoModeListener from "./AutoModeListener";
import BackgroundLayer from "./BackgroundLayer";
import EventLayer from "./EventLayer";
import ExtraLayerTagsLayer from "./ExtraLayerTagsLayer";
import IsFollowingVehicleChangeListener from "./IsFollowingVehicleChangeListener";
import LinesChangeListener from "./LinesChangeListener";
import MapClickListener from "./MapClickListener";
import NotificationIconLayer from "./NotificationIconLayer";
import NotificationsLayer from "./NotificationsLayer";
import RealtimeLayer from "./RealtimeLayer";
import SelectedLineChangeListener from "./SelectedLineChangeListener";
import SevLayer from "./SevLayer";
import StationsLayer from "./StationsLayer";

import "./LiveMap.scss";

const propTypes = {
  auto: PropTypes.bool,
  backgroundLayer: PropTypes.instanceOf(MaplibreLayer),
  centerStation: PropTypes.instanceOf(Feature),
  children: PropTypes.node,
  config: PropTypes.object.isRequired,
  debug: AppPropTypes.debugMode,
  dispatchSetIsFollowingVehicle: PropTypes.func.isRequired,
  dispatchSetSelectedLine: PropTypes.func.isRequired,
  dispatchSetSelectedVehicles: PropTypes.func.isRequired,
  dispatchSetStation: PropTypes.func.isRequired,
  dispatchSubscribeDeletedVehicles: PropTypes.func.isRequired,
  dispatchSubscribeStations: PropTypes.func.isRequired,
  dispatchTrackEvent: PropTypes.func.isRequired,
  dispatchUnsubscribeDepartures: PropTypes.func.isRequired,
  hideOthers: PropTypes.bool,
  highlightStation: PropTypes.instanceOf(Feature),
  history: PropTypes.object,
  line: PropTypes.string,
  lines: PropTypes.arrayOf(PropTypes.object).isRequired,
  map: PropTypes.instanceOf(Map).isRequired,
  mapStyle: PropTypes.object,
  mode: PropTypes.string,
  notifications: PropTypes.arrayOf(PropTypes.object),
  realtimeLayer: PropTypes.instanceOf(TralisLayer),
  schematicBgLayer: PropTypes.instanceOf(MaplibreLayer),
  selectedLine: PropTypes.object,
  selectedVehicles: PropTypes.arrayOf(PropTypes.object),
  station: PropTypes.instanceOf(Feature),
  topographicBgLayer: PropTypes.instanceOf(MaplibreLayer),
  train: PropTypes.string,
  trainAuto: PropTypes.bool,
};

const defaultProps = {
  auto: false,
  centerStation: null,
  children: null,
  debug: false,
  hideOthers: false,
  highlightStation: null,
  history: null,
  line: null,
  mapStyle: {},
  mode: modes.SCHEMATIC,
  notifications: [],
  schematicBgLayer: null,
  selectedLine: null,
  selectedVehicles: null,
  station: null,
  topographicBgLayer: null,
  train: null,
  trainAuto: false,
};

// Use for the source and the style layer.
let highlightLayerId;
const format = new GeoJSON();
const formatToLonLat = new GeoJSON({
  dataProjection: "EPSG:3857",
  featureProjection: "EPSG:4326",
});

class LiveMap extends Component {
  constructor(props) {
    super(props);
    const { map } = this.props;
    this.map = map;

    this.onTrajectoryMessage = this.onTrajectoryMessage.bind(this);
    this.resetFullTrajectoryRequest =
      this.resetFullTrajectoryRequest.bind(this);
  }

  /**
   * Load map and tracker.
   * The used websocket config is based on url parameter 'mode'.
   */
  componentDidMount() {
    this.map.setTarget(document.getElementById("map"));

    this.loadTracker();
    this.onModeChange();

    this.key = this.map.on("moveend", () => {
      window.clearTimeout(this.mapMoveTimeout);

      // We use a timeout to ensure
      this.mapMoveTimeout = window.setTimeout(() => {
        this.onMapMoved();
      }, 50);
    });
  }

  componentDidUpdate(prevProps) {
    const {
      centerStation,
      debug,
      highlightStation,
      mode,
      selectedVehicles,
      station,
    } = this.props;

    const nextStation = station || centerStation;

    // Remove max/min zoom when in debug mode
    if (debug && prevProps.debug !== debug) {
      this.map.getView().setMaxZoom(100);
      this.map.getView().setMinZoom(0);
    }

    // extent handling
    if (nextStation && nextStation.get("mode") === prevProps.mode) {
      const ext = station.getGeometry().getExtent();
      this.map.getView().setCenter(getCenter(ext));
      station.set("mode", null); // center only once
    }

    this.loadTracker();

    // mode handling
    if (mode && prevProps.mode !== mode) {
      this.onModeChange(prevProps);
    }

    // station highlight
    if (
      station &&
      highlightStation &&
      highlightStation !== prevProps.highlightStation
    ) {
      const ext = highlightStation.getGeometry().getExtent();
      const center = getCenter(ext);
      const duration = station.get("animationDuration") || 500;

      if (center[0] && center[1]) {
        this.disableEventTrackingOnce = true;
        this.map.getView().animate({
          center,
          duration,
        });
      }
    }

    if (selectedVehicles !== prevProps.selectedVehicles) {
      this.onSelectedVehiclesChange();
    }
  }

  componentWillUnmount() {
    unByKey(this.key);
  }

  getHighlightStyleLayer({ name, stroke }) {
    const { config, mode } = this.props;

    // We don't reuse the same id otherwise the color changes before the geometry changes.
    highlightLayerId = `${name}-highlight`;
    return {
      id: highlightLayerId,
      paint: {
        "line-color": `${stroke}`,
        "line-width": config[mode].paintLineWidthHighlight,
      },
      source: highlightLayerId,
      type: "line",
    };
  }

  /**
   * Display fulltrajectory of a vehicle.
   */
  highlightVehicle(selectedVehicleId) {
    const {
      backgroundLayer,
      config,
      dispatchSetSelectedLine,
      hideOthers,
      line,
      lines,
      mode,
      train,
    } = this.props;

    const { highlightFullTrajectory } = config[mode];
    if (!highlightFullTrajectory) {
      return;
    }

    // Make sure we don't request too much time the full trajectory.
    if (this.lastFullTrajRequestedId === selectedVehicleId + mode) {
      // debugger;
      return;
    }

    this.lastFullTrajRequestedId = selectedVehicleId + mode;
    tralisAPI
      .getFullTrajectory(selectedVehicleId, mode)
      .then((message) => {
        const featureCollection = message.content;
        if (!featureCollection || !featureCollection.features?.length) {
          return;
        }

        const { line_name: lineName, stroke } =
          featureCollection.features[0].properties;

        if (train && !line && hideOthers) {
          // We highlight the line
          const selectedLine = lines.find(({ name }) =>
            areLineNameEqual(name, lineName),
          );
          dispatchSetSelectedLine(selectedLine);

          // TODO we need to define if the getFullTrajectory return always the entire line.
          // the following 'else' could be useless.
          // and if not we have to add the stations after the highlight of the full trajectory.
        } else {
          const bgLayer = backgroundLayer;

          if (bgLayer) {
            const source = {
              // Covert to 4326 before adding the source.
              data: format.writeFeaturesObject(
                formatToLonLat.readFeatures(featureCollection),
              ),
              type: "geojson",
            };

            const styleLayer = this.getHighlightStyleLayer({
              name: lineName,
              stroke,
            });

            addSourceAndLayers(
              bgLayer,
              styleLayer.id,
              source,
              styleLayer,
              config[mode].beforeLayerIdForHighlight,
            );
          }
        }
      })
      .catch((error) => {
        if (error.type === "close") {
          // Bug only when switching mode
          // Connection closes when a 2nd BBOX after the first one has not yet been handled after a new ws connection.
          // In that case the fulltrajetcory requests failed so we have to retry in 2nd hoping the connection will be re-established.
          this.lastFullTrajRequestedId = null;
          setTimeout(() => {
            this.highlightVehicle(selectedVehicleId);
          }, 2000);
          return;
        }
      });
  }

  /**
   * Initalize the tracker.
   */
  loadTracker() {
    const { mode, realtimeLayer, train } = this.props;
    if (!realtimeLayer) {
      if (train) {
        tralisAPI.subscribeTrajectory(mode, this.onTrajectoryMessage);
      }
    }
  }

  /**
   * Update extent based parameters if the map was moved.
   */
  onMapMoved(silent = false) {
    const {
      config,
      dispatchTrackEvent,
      hideOthers,
      mode,
      schematicBgLayer,
      selectedLine,
    } = this.props;

    let qparams = qs.parse(window.location.search) || {};

    const c = this.map
      .getView()
      .getCenter()
      .map((cc) => Math.round(cc));
    qparams = Object.assign(qparams, {
      x: c[0],
      y: c[1],
      z: Math.round(this.map.getView().getZoom() * 100) / 100,
    });

    if (config.xyzParam) {
      qparams = qs.stringify(qparams);
    }

    if (silent === false && this.disableEventTrackingOnce === false) {
      dispatchTrackEvent(
        TRACK_EVENT_CATEGORY_MAP,
        "moved",
        JSON.stringify(qparams),
      );
    }
    this.disableEventTrackingOnce = false;

    if (
      config[mode].usePeripherieIcons &&
      mode === modes.SCHEMATIC &&
      schematicBgLayer?.map
    ) {
      const addGreyBg = config[mode].useHideOthersBg && hideOthers;
      addPeripherieLineIcons(
        schematicBgLayer,
        addGreyBg ? selectedLine?.name : undefined,
      );
    }
  }

  onModeChange(prevProps) {
    const {
      auto,
      config,
      debug,
      dispatchSubscribeDeletedVehicles,
      dispatchSubscribeStations,
      mode,
      station,
      train,
    } = this.props;
    const {
      dataExtent,
      getZoomFromPreviousMode,
      maxZoom,
      maxZoomForAutoMode,
      minZoom,
      minZoomForAutoMode,
      x,
      y,
    } = config[mode];

    // Reset the extent
    const view = this.map.getView();
    this.map.setView(
      new View({
        center: view.getCenter(),
        extent: buffer(dataExtent, dataExtent[2] - dataExtent[0]),
        rotation: view.getRotation(),
        zoom: view.getZoom(),
      }),
    );

    const prevZoom = this.map.getView().getZoom();

    if (!debug) {
      this.map.getView().setMinZoom(minZoom);
      this.map.getView().setMaxZoom(maxZoom);
    }

    // Only call getZoomFromPreviousMode when the component did update, not on load.
    let zoom = prevProps ? getZoomFromPreviousMode(prevZoom) : prevZoom;

    // In auto mode we zoom first to the minZoom level then 10 second later to the maxZoom level.
    if (auto) {
      zoom = station ? maxZoomForAutoMode : minZoomForAutoMode;
    }

    // Update view
    if (prevProps) {
      this.map.getView().setZoom(zoom);
      this.map.getView().setCenter([x, y]);
    }

    // Update view
    if (train) {
      tralisAPI.subscribeTrajectory(mode, this.onTrajectoryMessage);
    }

    dispatchSubscribeDeletedVehicles();
    dispatchSubscribeStations();

    // Highlight the current selected vehicle in the new mode
    this.onSelectedVehiclesChange();
  }

  onSelectedVehiclesChange() {
    const {
      auto,
      debug,
      dispatchSetIsFollowingVehicle,
      dispatchSetStation,
      dispatchUnsubscribeDepartures,
      realtimeLayer,
      schematicBgLayer,
      selectedVehicles,
      topographicBgLayer,
    } = this.props;

    // End the following of trains
    dispatchSetIsFollowingVehicle(false);

    // Clear the line feature from the topographic map.
    const vectorLayer = realtimeLayer?.vectorLayer;

    if (vectorLayer) {
      vectorLayer.getSource().clear(true);
    }

    // Remove higlighting before adding a new highlight.
    cleanHighlight(schematicBgLayer, highlightLayerId);
    cleanHighlight(topographicBgLayer, highlightLayerId);

    if (selectedVehicles && selectedVehicles.length && !auto) {
      dispatchSetStation();
      dispatchUnsubscribeDepartures();
    }

    if (selectedVehicles && selectedVehicles.length === 1) {
      if (debug) {
        console.log(...selectedVehicles);
      }

      const [selectedVehicle] = selectedVehicles;
      this.highlightVehicle(selectedVehicle.train_id);
    }
  }

  onTrajectoryMessage(data) {
    const {
      config,
      dispatchSetIsFollowingVehicle,
      dispatchSetSelectedVehicles,
      hideOthers,
      realtimeLayer,
      selectedVehicles,
      train,
      trainAuto,
    } = this.props;

    if (!train || !data.content || !realtimeLayer) {
      return;
    }

    let selectedVehicleId = null;
    if (selectedVehicles.length === 1) {
      selectedVehicleId = selectedVehicles[0].train_id;
    }

    if (selectedVehicleId) {
      tralisAPI.unsubscribeTrajectory(this.onTrajectoryMessage);
      return;
    }

    const vehicles = Object.values(realtimeLayer?.trajectories || [])
      .filter((feature) => {
        const vehicle = feature.properties;
        if (selectedVehicleId) {
          return isVehicleToHighlight(vehicle, selectedVehicleId);
        }
        if (trainAuto) {
          return config.findTrainAuto(feature);
        }
        return isVehicleToHighlight(vehicle, train);
      })
      .map((feature) => feature.properties);

    if (!selectedVehicleId && trainAuto && vehicles.length < 4) {
      return;
    }

    if (vehicles.length) {
      const vehicle = trainAuto ? getRandomVehicle(vehicles) : vehicles[0];
      // Update the tracker filter.
      if (realtimeLayer && hideOthers) {
        realtimeLayer.filter = (feature) => {
          const vehi = feature.properties;
          return isVehicleToHighlight(vehi, vehicle.train_id);
        };
      }
      dispatchSetSelectedVehicles([vehicle]);
      dispatchSetIsFollowingVehicle(true);

      tralisAPI.unsubscribeTrajectory(this.onTrajectoryMessage);
    }
  }

  render() {
    const { children, mapStyle } = this.props;

    return (
      <div id="map" style={mapStyle}>
        <BackgroundLayer />
        <StationsLayer />
        <RealtimeLayer />
        <SevLayer />
        <ExtraLayerTagsLayer />
        <NotificationsLayer />
        <NotificationIconLayer />
        <EventLayer />
        <MapClickListener
          onBeforeSelectVehicles={this.resetFullTrajectoryRequest}
        />
        <LinesChangeListener />
        <SelectedLineChangeListener />
        <AutoModeListener />
        <IsFollowingVehicleChangeListener />
        {children}
      </div>
    );
  }

  /**
   * Force a re-fetch of the full trajectory next time this vehicle is
   * highlighted, even if it was already the one highlighted before.
   * Called by MapClickListener right before selecting a vehicle.
   */
  resetFullTrajectoryRequest() {
    this.lastFullTrajRequestedId = null;
  }
}

const mapStateToProps = (state) => ({
  auto: state.auto,
  backgroundLayer: state.backgroundLayer,
  centerStation: state.centerStation,
  config: state.config,
  debug: state.debug,
  hideOthers: state.hideOthers,
  highlightStation: state.highlightStation,
  line: state.line,
  lines: state.lines,
  map: state.map,
  mode: state.mode,
  notifications: state.notifications,
  realtimeLayer: state.realtimeLayer,
  schematicBgLayer: state.schematicBgLayer,
  selectedLine: state.selectedLine,
  selectedVehicles: state.selectedVehicles,
  station: state.station,
  topographicBgLayer: state.topographicBgLayer,
  train: state.train,
  trainAuto: state.trainAuto,
});

const mapDispatchToProps = {
  dispatchSetIsFollowingVehicle: setIsFollowingVehicle,
  dispatchSetSelectedLine: setSelectedLine,
  dispatchSetSelectedVehicles: setSelectedVehicles,
  dispatchSetStation: setStation,
  dispatchSubscribeDeletedVehicles: subscribeDeletedVehicles,
  dispatchSubscribeStations: subscribeStations,
  dispatchTrackEvent: trackEvent,
  dispatchUnsubscribeDepartures: unsubscribeDepartures,
};

LiveMap.propTypes = propTypes;
LiveMap.defaultProps = defaultProps;

export default connect(mapStateToProps, mapDispatchToProps)(LiveMap);
